import type { GameState, Tile } from '@completto/shared';
import { Store } from './base-store.ts';

// === Session ===
export type SessionState = {
  nickname: string;
};

const NICKNAME_KEY = 'completto:nickname';

function readNickname(): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(NICKNAME_KEY) ?? '';
}

export const sessionStore = new Store<SessionState>({
  nickname: readNickname(),
});

export function setNickname(nickname: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(NICKNAME_KEY, nickname);
  }
  sessionStore.set({ nickname });
}

// === Socket ===
export type SocketState = {
  status: 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
  socketId: string | null;
  reconnectAttempt: number;
};

export const socketStore = new Store<SocketState>({
  status: 'disconnected',
  socketId: null,
  reconnectAttempt: 0,
});

// === Room (Phase 4에서 사용) ===
export type RoomSummary = {
  roomCode: string;
  myPlayerId: string;
};

export const roomStore = new Store<RoomSummary | null>(null);

// === Game ===
export const gameStore = new Store<GameState | null>(null);

// === UI ===
export type UiMode =
  | { kind: 'IDLE' }
  | { kind: 'POOL_PICK' }
  | {
      kind: 'PLACEMENT';
      // PLACE 또는 DISCARD를 결정하기 직전 상태
      tile: Tile;
      resolvedNumber: number | null; // 회전 결정 결과 (null이면 아직 미결정)
      selectedIdx: number | null;
    }
  | { kind: 'ROTATION_PICK'; tile: Tile; targetIdx: number }
  | { kind: 'SWAP_FROM' }
  | { kind: 'SWAP_TO'; fromIdx: number };

export type ToastMessage = {
  id: string;
  level: 'info' | 'warn' | 'error';
  text: string;
  expiresAt: number;
};

export type PoolFilterMode = 'ALL' | 'PLACEABLE_ONLY';

export type UiState = {
  uiMode: UiMode;
  toasts: ToastMessage[];
  sendingMove: boolean; // 멀티플레이어용 (Phase 5)
  poolFilter: PoolFilterMode;
};

export const uiStore = new Store<UiState>({
  uiMode: { kind: 'IDLE' },
  toasts: [],
  sendingMove: false,
  poolFilter: 'ALL',
});

export function setPoolFilter(mode: PoolFilterMode): void {
  uiStore.set((s) => ({ ...s, poolFilter: mode }));
}

export function setSendingMove(sending: boolean): void {
  uiStore.set((s) => (s.sendingMove === sending ? s : { ...s, sendingMove: sending }));
}

const TOAST_DURATION_MS = 3000;

export function pushToast(text: string, level: ToastMessage['level'] = 'info'): void {
  const t: ToastMessage = {
    id: crypto.randomUUID(),
    level,
    text,
    expiresAt: Date.now() + TOAST_DURATION_MS,
  };
  uiStore.set((s) => ({ ...s, toasts: [...s.toasts, t] }));
  setTimeout(() => {
    uiStore.set((s) => ({ ...s, toasts: s.toasts.filter((x) => x.id !== t.id) }));
  }, TOAST_DURATION_MS);
}

export function setUiMode(uiMode: UiMode): void {
  uiStore.set((s) => ({ ...s, uiMode }));
}
